import { useCallback, useEffect, useState } from "react";
import { Icon } from "@mypos/ui";
import { api } from "../lib/api";

// Mirrors the API's LicenseStatus (apps/api/src/license). Only the fields the
// banner needs are typed. The endpoint requires a manager + setup permission,
// so a 401/403/offline simply yields no banner (the catch sets null).
type LicenseStatus = {
  state: "valid" | "trial" | "invalid";
  reason: string;
  warnings: string[];
  customer: string | null;
  tillsMax: number | null;
  tillsUsed: number;
};

/**
 * Non-blocking license notice shown across the top of the Manager PWA.
 *
 * Mirrors OfflineBanner. The server's boot gate is the only hard enforcement —
 * a running server is always allowed to sell — so this banner's whole job is to
 * nag the owner before the trial lapses or while they're over their till limit.
 * A healthy, in-limit licensed server shows nothing.
 */
export function LicenseBanner() {
  const [status, setStatus] = useState<LicenseStatus | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.get<LicenseStatus>("/license"));
    } catch {
      // No permission, server unreachable, or an older API — say nothing.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void load();
    // Re-check when the operator returns to the tab — e.g. right after a key is
    // activated on the server the warning should clear without a full reload.
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!status) return null;

  const overLimit = status.tillsMax !== null && status.tillsUsed > status.tillsMax;
  // Healthy, fully-licensed, within limits → stay out of the way.
  if (status.state === "valid" && status.warnings.length === 0 && !overLimit) {
    return null;
  }

  const danger = status.state === "invalid" || overLimit;
  const lines = status.warnings.length > 0 ? status.warnings : [status.reason];
  const palette = danger
    ? { bg: "var(--danger-soft, #FCEDEC)", fg: "var(--danger, #B42318)", icon: "shield-alert" as const }
    : { bg: "var(--warn-soft, #FDF1E3)", fg: "var(--warn, #B8470C)", icon: "circle-alert" as const };

  return (
    <div
      role="status"
      style={{
        padding: "8px 16px",
        background: palette.bg,
        color: palette.fg,
        borderBottom: "1px solid var(--border, #E8DFCE)",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <Icon name={palette.icon} size="sm" />
      <strong>{danger ? "License problem" : "License notice"}</strong>
      <span style={{ color: "var(--ink-2, #55504A)" }}>
        — {lines.join(" · ")}
        {status.state === "trial"
          ? " Activate your MyPOS license key on the server to keep running."
          : ""}
      </span>
    </div>
  );
}
